import { passThroughAxios } from '@/api/server/passThrough.axios'

const ManagerApprovalService = {
  /**
   * Get orders pending manager approval or approved orders pending admin review
   * @param {number} page - Page number for pagination
   * @param {number} size - Page size
   * @param {string} searchText - Search query (optional)
   * @param {string} searchOnPonId - Search on PON user ID (optional filter)
   * @returns {Promise} API response with quotes
   */
  async getApprovalOverviewOrders(page = 0, size = 20, searchText = null, searchOnPonId = null) {
    const params = { page, size }

    // Add optional parameters
    if (searchText) params.searchText = searchText
    if (searchOnPonId) params.searchOnPonId = searchOnPonId

    try {
      return await passThroughAxios.get('order-approval/review', { params })
    } catch (error) {
      console.error('Error fetching manager approvals:', error)
      throw error
    }
  },

  /**
   * Change order status (approve/reject)
   * @param {number} quoteId - Quote ID to update
   * @param currentQuoteStatus - Current status of the Quote
   * @param {string} action - Action to perform ('MANAGER_APPROVE' or 'MANAGER_REJECT')
   * @param {string} reason - Reason for the action (required for reject, optional for approve)
   * @returns {Promise} API response
   */
  async changeOrderStatus(quoteId, currentQuoteStatus, action, reason = null) {
    const url = this.determineChangeStatusUrl(quoteId, currentQuoteStatus)

    const data = { action, reason }

    try {
      return await passThroughAxios.post(url, data)
    } catch (error) {
      console.error('Error changing order status:', error)
      throw error
    }
  },
  /**
   * When the QUOTE has the Status TO_APPROVE we can set it to APPROVE/REJECT status using the
   * endpoint order-approval/${quoteId}/status-change
   * When the QUOTE has the Status APPROVED we can set it to PENDING/REJECT status using the
   * endpoint order-approval/${quoteId}/status-change-admin
   * @param {number} quoteId - Quote ID to approve
   * @param currentQuoteStatus - Current status of the Quote
   * @returns {string}
   */
  determineChangeStatusUrl(quoteId, currentQuoteStatus) {
    if (currentQuoteStatus === 'TO_APPROVE') {
      return `order-approval/${quoteId}/status-change`
    }
    if (currentQuoteStatus === 'APPROVED') {
      return `order-approval/${quoteId}/status-change-admin`
    }
    throw new Error("Can't determine the change status url for: " + currentQuoteStatus)
  },

  /**
   * Approve an order
   * @param {number} quoteId - Quote ID to approve
   * @param currentQuoteStatus - Current status of the Quote
   * @param {string} reason - Optional reason for approval
   * @returns {Promise} API response
   */
  async approveOrder(quoteId, currentQuoteStatus, reason = null) {
    return this.changeOrderStatus(quoteId, currentQuoteStatus, 'APPROVE', reason)
  },

  /**
   * Reject an order
   * @param {number} quoteId - Quote ID to reject
   * @param currentQuoteStatus - Current status of the Quotes
   * @param {string} reason - Required reason for rejection
   * @returns {Promise} API response
   */
  async rejectOrder(quoteId, currentQuoteStatus, reason) {
    if (!reason || reason.trim() === '') {
      throw new Error('Rejection reason is required')
    }
    return this.changeOrderStatus(quoteId, currentQuoteStatus, 'REJECT', reason)
  },
}

export default ManagerApprovalService
